import { isFulfilled } from "@reduxjs/toolkit";
import { format, isValid } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../app/hooks";
import { isoFormat, today } from "../common/helpers/formatDateTime";
import { SalaryType } from "../features/orders/types/salaryType";
import { SalaryComponent } from "../features/tesSalaryComponents/tesSalaryComponentsAPI";
import { fetchTesSalaryComponents } from "../features/tesSalaryComponents/tesSalaryComponentsSlice";

type Props = {
  validFrom?: Date | null;
  tesId: string;
  salaryType?: SalaryType;
  defaultComponentValues?: { [key: string]: string };
  defaultRangeValues?: SalaryFormValues[];
};

export interface GroupedSalaryComponents {
  components: {
    baseComponents: SalaryComponent[];
    personalComponents: SalaryComponent[];
    extraComponents: SalaryComponent[];
  };
}

export type SalaryFormValues = {
  from?: string;
  components: { [code: string]: string };
  totalSalary: string;
  tesSalaryTableSelection?: {
    tesSalaryTableId: string;
    rowId: string;
    columnId: string;
    mode:
      | "FULL_SALARY"
      | "WITH_COMPS"
      | "INFO_ONLY";
  } | null;
};

const validateDate = (date: Date | null) => {
  if (!date) return null;

  const formattedDate = isValid(date)
    ? format(new Date(date), isoFormat)
    : null;

  if (!formattedDate) return null;

  const dateParts = formattedDate.split("-");
  const yearContainsLeadingZeros = /^0+/.test(dateParts?.[0]);
  const isValidDate =
    dateParts && formattedDate.length === 10 && !yearContainsLeadingZeros;

  if (isValidDate) return formattedDate;

  return null;
};

export const isExtraComponent = (code: string) => {
  let lowerCase = code.toLowerCase();
  return (
    lowerCase === "luvanhaltijanedustajanlisa" ||
    lowerCase === "palkkaryhman_mukainen_palkka_(tiedoksi)"
  );
};

export const groupSalaryComponents = (data: any[]) => {
  return data.reduce(
    (result, item) => {
      const salaryComponent: SalaryComponent = {
        code: item.code,
        allowsZero: item.allowsZero,
        isPartOfBaseSalary: item.isPartOfBaseSalary,
      };

      if (item.infoText) {
        salaryComponent.infoText = item.infoText;
      }

      if (item.isPartOfBaseSalary && !isExtraComponent(item.code)) {
        result = {
          ...result,
          baseComponents: [...result.baseComponents, salaryComponent],
        };
      } else if (!item.isPartOfBaseSalary && !isExtraComponent(item.code)) {
        result = {
          ...result,
          personalComponents: [...result.personalComponents, salaryComponent],
        };
      } else {
        result = {
          ...result,
          extraComponents: [...result.extraComponents, salaryComponent],
        };
      }

      return result;
    },
    {
      baseComponents: [],
      personalComponents: [],
      extraComponents: [],
    },
  );
};

const useTesSalaryComponents = ({
  validFrom,
  tesId,
  salaryType,
  defaultComponentValues,
  defaultRangeValues,
}: Props) => {
  const [salaryComponents, setSalaryComponents] =
    useState<GroupedSalaryComponents>();
  const [salaryFormDefaultValues, setSalaryFormDefaultValues] =
    useState<SalaryFormValues>({ components: {}, totalSalary: "" });
  const [salaryRangeDefaultValues, setSalaryRangeDefaultValues] = useState<
    SalaryFormValues[]
  >([]);
  const [salaryComponentsStatus, setSalaryComponentsStatus] = useState<
    "idle" | "loading" | "completed"
  >("idle");

  const dispatch = useAppDispatch();

  const getSalaryComponentDefaultValues = (data: SalaryComponent[]) =>
    data.reduce(
      (result, { code }) => {
        result[code] = "";
        return result;
      },
      {} as { [key: string]: string },
    );

  const validDate = useMemo(
    () =>
      validFrom !== undefined ? validateDate(validFrom) : today(isoFormat),
    [validFrom],
  );

  useEffect(() => {
    if (!!tesId) {
      // if validFrom is passed, use it, otherwise use today's date

      if (validDate) {
        setSalaryComponentsStatus("loading");
        dispatch(
          fetchTesSalaryComponents({
            tesId,
            date: validDate,
            salaryType: salaryType?.toLowerCase() || SalaryType.Hourly,
          }),
        ).then((action) => {
          if (action && isFulfilled(action)) {
            const groupped = {
              components: groupSalaryComponents(action.payload),
            };
            setSalaryComponents(groupped);

            if (!defaultComponentValues && !defaultRangeValues) {
              const salaryFormDefaultValues = getSalaryComponentDefaultValues(
                action.payload,
              );
              setSalaryFormDefaultValues({
                components: { ...salaryFormDefaultValues },
                totalSalary: "",
              });
            } else {
              if (defaultRangeValues) {
                setSalaryRangeDefaultValues(defaultRangeValues);
              }
              if (defaultComponentValues) {
                setSalaryFormDefaultValues({
                  components: { ...defaultComponentValues },
                  totalSalary: "",
                });
              }
            }
          }
          setSalaryComponentsStatus("completed");
        });
      }
    }
  }, [
    tesId,
    validDate,
    salaryType,
    defaultComponentValues,
    defaultRangeValues,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      setSalaryComponentsStatus("idle");
    };
  }, []);

  return {
    salaryComponents,
    salaryFormDefaultValues,
    salaryRangeDefaultValues,
    salaryComponentsStatus,
  };
};

export default useTesSalaryComponents;
